import { motion } from "framer-motion";
import { ArrowRight, ArrowDown } from "lucide-react";
import { TEST_IDS } from "@/lib/testIds";
import { waLink } from "@/lib/contact";

const PROFILE_IMG = "/assets/profile.png";

const institutions = [
  {
    slug: "barca",
    title: "FC Barcelona Innovation Hub",
    subtitle: "Profesyonel Diploma",
    logo: "/assets/logo-fcb.png",
    monogram: "FCB",
  },
  {
    slug: "cerrahpasa",
    title: "İÜ Cerrahpaşa Tıp Fakültesi",
    subtitle: "Klinik Staj",
    logo: null,
    monogram: "İÜC",
  },
  {
    slug: "bahcesehir",
    title: "Bahçeşehir Üniversitesi",
    subtitle: "Beslenme & Diyetetik",
    logo: "/assets/logo-bahcesehir.png",
    monogram: "BAU",
  },
];

const scrollTo = (id) => {
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
};

const container = {
  hidden: { opacity: 0 },
  show: { opacity: 1, transition: { staggerChildren: 0.08, delayChildren: 0.1 } },
};
const item = {
  hidden: { opacity: 0, y: 22 },
  show: { opacity: 1, y: 0, transition: { duration: 0.65, ease: [0.22, 1, 0.36, 1] } },
};

export const Hero = () => {
  return (
    <section
      id="anasayfa"
      className="relative pt-32 md:pt-36 pb-12 md:pb-16 overflow-hidden bg-mesh-mint grain"
    >
      {/* Floating blobs */}
      <div className="blob" style={{ width: 440, height: 440, top: -120, right: -120, background: "radial-gradient(circle, rgba(14,165,233,0.55), transparent 70%)" }} />
      <div className="blob delay-1" style={{ width: 380, height: 380, bottom: -160, left: -120, background: "radial-gradient(circle, rgba(16,185,129,0.45), transparent 70%)" }} />

      <div className="relative max-w-7xl mx-auto px-6 md:px-12">
        <motion.div
          variants={container}
          initial="hidden"
          animate="show"
          className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-10 items-center"
        >
          {/* LEFT — content */}
          <div className="lg:col-span-7 order-2 lg:order-1">
            <motion.div variants={item} className="flex items-center gap-3 mb-5">
              <span className="block h-px w-10 bg-gradient-to-r from-[#10B981] to-[#0EA5E9]" />
              <span className="text-[10px] font-extrabold uppercase tracking-[0.32em] text-[#047857]">
                Diyetisyen · Istanbul
              </span>
            </motion.div>

            <motion.h1
              variants={item}
              className="font-heading font-black text-[3.5rem] sm:text-7xl lg:text-[6.5rem] xl:text-[7.5rem] leading-[0.92] tracking-tight text-[#0F2D2A]"
            >
              Berke
              <br />
              Akdağ
            </motion.h1>

            <motion.div variants={item} className="mt-6 flex flex-wrap gap-2">
              {[
                "Klinik Diyetisyen",
                "Bilimsel Kanıta Dayalı",
                "Tıbbi Beslenme",
                "Sporcu Diyetisyeni",
              ].map((tag) => (
                <span
                  key={tag}
                  className="text-[11px] md:text-xs font-extrabold uppercase tracking-[0.14em] px-3 py-1.5 bg-white/75 backdrop-blur border border-[#0F2D2A]/10 text-[#0F2D2A] rounded-full shadow-sm"
                >
                  {tag}
                </span>
              ))}
            </motion.div>

            <motion.p
              variants={item}
              className="mt-7 max-w-2xl text-base md:text-lg leading-relaxed text-[#1E4842]"
            >
              <span className="font-black text-[#0F2D2A]">
                Cerrahpaşa Tıp Fakültesi&apos;ndeki klinik deneyimimi
              </span>
              , <span className="font-black text-[#0F2D2A]">FC Barcelona</span>{" "}
              vizyonu ve disipliniyle birleştirdim.
            </motion.p>

            <motion.div variants={item} className="mt-8 flex flex-col sm:flex-row gap-3">
              <a
                href={waLink()}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={TEST_IDS.hero.primaryCta}
                className="btn-shimmer group inline-flex items-center justify-center gap-3 px-7 py-3.5 text-xs md:text-sm font-extrabold uppercase tracking-[0.16em] text-white rounded-full bg-gradient-to-r from-[#10B981] via-[#0AB7C5] to-[#0EA5E9] shadow-[0_18px_40px_-18px_rgba(14,165,233,0.6)] hover:shadow-[0_24px_50px_-18px_rgba(14,165,233,0.75)] transition-all duration-300 hover:-translate-y-0.5"
              >
                Randevu Al
                <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
              </a>
              <button
                type="button"
                onClick={() => scrollTo("hizmetler")}
                data-testid={TEST_IDS.hero.secondaryCta}
                className="group inline-flex items-center justify-center gap-3 px-7 py-3.5 text-xs md:text-sm font-extrabold uppercase tracking-[0.16em] rounded-full border border-[#0F2D2A]/25 text-[#0F2D2A] bg-white/40 backdrop-blur hover:bg-white hover:border-[#0F2D2A] transition-all duration-300"
              >
                Hizmetleri Keşfet
                <ArrowDown className="w-4 h-4 transition-transform duration-300 group-hover:translate-y-1" />
              </button>
            </motion.div>
          </div>

          {/* RIGHT — Profile + Logos */}
          <motion.div
            variants={item}
            className="lg:col-span-5 order-1 lg:order-2"
          >
            <div className="relative grid grid-cols-[1.15fr_1fr] gap-3 md:gap-4 items-center">
              {/* PROFILE */}
              <div className="relative">
                {/* Radial glow */}
                <div
                  aria-hidden
                  className="absolute inset-0 pointer-events-none -z-0"
                  style={{
                    background:
                      "radial-gradient(55% 60% at 45% 60%, rgba(14,165,233,0.32), transparent 70%), radial-gradient(45% 50% at 60% 75%, rgba(16,185,129,0.25), transparent 70%)",
                  }}
                />
                <img
                  src={PROFILE_IMG}
                  alt="Berke Akdağ"
                  data-testid={TEST_IDS.hero.profileImage}
                  className="relative z-10 w-full h-auto max-h-[480px] object-contain object-bottom drop-shadow-[0_30px_30px_rgba(15,45,42,0.25)]"
                  loading="eager"
                />
              </div>

              {/* LOGOS — stacked, breathable */}
              <div className="flex flex-col gap-3 self-center">
                <motion.div
                  initial={{ opacity: 0, x: 16 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.35 }}
                  className="flex items-center gap-2 mb-1"
                >
                  <span className="block h-px w-5 bg-gradient-to-r from-[#10B981] to-[#0EA5E9]" />
                  <span className="text-[9px] font-extrabold uppercase tracking-[0.28em] text-[#047857]">
                    Klinik Eğitim
                  </span>
                </motion.div>
                {institutions.map((inst, i) => (
                  <motion.div
                    key={inst.slug}
                    initial={{ opacity: 0, x: 18 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.45 + i * 0.09, duration: 0.55 }}
                    data-testid={TEST_IDS.hero.logo(inst.slug)}
                    className="bg-white/90 backdrop-blur border border-[#0F2D2A]/10 rounded-xl p-2.5 md:p-3 flex items-center gap-2.5 md:gap-3 shadow-[0_8px_24px_-16px_rgba(15,45,42,0.3)] hover:shadow-[0_14px_30px_-16px_rgba(14,165,233,0.5)] hover:-translate-y-0.5 transition-all duration-300"
                  >
                    <div className="w-11 h-11 md:w-12 md:h-12 shrink-0 flex items-center justify-center bg-white p-1.5 rounded-lg border border-[#0F2D2A]/8">
                      {inst.logo ? (
                        <img
                          src={inst.logo}
                          alt={inst.title}
                          className="max-w-full max-h-full object-contain"
                          loading="eager"
                        />
                      ) : (
                        <span className="font-heading font-black text-sm bg-gradient-to-br from-[#10B981] to-[#0EA5E9] bg-clip-text text-transparent">
                          {inst.monogram}
                        </span>
                      )}
                    </div>
                    <div className="min-w-0 leading-tight">
                      <p className="text-[10px] md:text-[11px] font-extrabold uppercase tracking-[0.04em] text-[#0F2D2A]">
                        {inst.title}
                      </p>
                      <p className="text-[9px] md:text-[10px] mt-1 font-bold text-[#0EA5E9] uppercase tracking-[0.1em]">
                        {inst.subtitle}
                      </p>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          </motion.div>
        </motion.div>
      </div>

      {/* Bottom transition */}
      <div
        aria-hidden
        className="absolute -bottom-px left-0 right-0 h-20 pointer-events-none"
        style={{
          background:
            "linear-gradient(to bottom, transparent 0%, rgba(245,250,248,0.7) 60%, #F5FAF8 100%)",
        }}
      />
    </section>
  );
};

export default Hero;
